

import { ArrowRight, ChevronRight } from "@mui/icons-material"
import { Box, BoxProps, Stack, StackProps, SxProps, Theme, Typography, TypographyProps, useTheme } from "@mui/material"

import { Link as GatsbyLink } from 'gatsby'
import React, { useMemo } from "react"
import { BlockRendererContent, TextBlockRenderer } from "../../../strapi/blockRenderer"
import { StrapiImageAsset } from "../../models"
import { TextItemProps } from "./text-item"
import { TextItemListComponent, TextItemListProps } from "./text-item-list"
import {GatsbyImageWithFallback} from "../../../../lib/gatsby/components/imageWithFallback";
import {LinkList} from "../../../../lib/mui/components/linkList";
import {LinkGroupProps} from "../../../../lib/mui/components/linkGroup";
import {Link} from "../../../../lib/mui/components/link";

const cardStyles = (theme: Theme, background: CardItemProps['Background'], borderColor: CardItemProps['BorderColor'], hasLink: boolean): SxProps => ({
    width: '100%',
    px: 3,
    py: !background && borderColor !== 'White' ? 0 : 3,
    height: '100%',
    border: borderColor === 'Primary' ? `1px solid ${theme.palette.primary.main}` : (borderColor === 'None' ? 'none' : (borderColor === 'White'  ? '1px solid white' : 'none')),
    pt: hasLink ? 3 : (borderColor === 'Primary' ? 3 : 3),
    // borderRadius: borderColor !== 'None' ? '20px' : undefined,
    borderRadius: '20px',
    background: background === 'Primary' ? 'linear-gradient(135deg, #6ABD45 0%, #004E17 100%)' : (!background || background === 'Default' || background === 'Light' ? (borderColor && borderColor !== 'None' ? undefined : theme.palette.background.paper)  : (background === 'White' ? 'white' : undefined)),
    color: (background === 'Primary' || borderColor === 'White') && background != 'White' ? 'white' : (background === 'White' ? theme.palette.brandBlack.main : undefined),
    ...(hasLink ? {
        '&:hover': {
            background: 'linear-gradient(135deg, #446337ff 0%, #073314ff 100%)',
            color: 'white',
            cursor: 'pointer',
            borderColor: 'transparent'
        }
    } : {

    })
})


export type CardItemProps = {
    id: string
    Heading?: string
    noParagraphSpacing?: boolean
    LinkUrl?: string
    Icon?: StrapiImageAsset
    IconElement?: React.ReactElement
    Body?: BlockRendererContent
    children?: React.ReactNode
    Background?: 'Default' | 'None' | 'Primary' | 'Light' | null | 'White'
    BorderColor?: 'Primary' | 'None' | null | 'White'
    Links?: any
    TextItems?: TextItemProps[]
    SlotProps?: {
        Heading?: TypographyProps,
        Body?: TypographyProps,
        ContainerStack?: StackProps
        RootStack?: StackProps
        Container?: BoxProps,
        TextItemList?: TextItemListProps['SlotProps']
    }
}

const CardComponentContent = ({ Heading, Icon, Body, Background, TextItems, SlotProps, Links, children, IconElement, BorderColor, noParagraphSpacing, LinkUrl }: CardItemProps) => {
    const theme = useTheme();

    const headingAndButtonOnly = !Body && (!Links || Links.length === 0) && (!TextItems || !TextItems.length) && !children;

    return (
        <Stack spacing={1.5} sx={{ height: '100%' }} direction={'row'} justifyContent={!headingAndButtonOnly ? 'flex-start' : 'space-between'} alignItems='center' {...SlotProps?.RootStack}>
            {Icon &&
              <Box sx={{width: 50, height: 50}}>
                  <Stack  sx={{ height: '100%' }} direction={'row'} justifyContent={ 'flex-start'} alignItems='center'>
                  <GatsbyImageWithFallback  url={Icon?.url}  loading="lazy" width={50} height={50} image={Icon.localFile?.childImageSharp?.gatsbyImageData} alt={Heading || Icon.name || 'image'} />
                  </Stack>
              </Box>
          }
            {IconElement && !headingAndButtonOnly ?      <Stack  sx={{ height: '100%' }} direction={'row'} justifyContent={ 'space-between'} alignItems='center'>
                {IconElement}
            </Stack> :  undefined}
            <Stack spacing={Links && Links.length > 0 ? 1.5 : 0.5} {...SlotProps?.ContainerStack}>
                {Heading && <Typography gutterBottom={false} variant="h6" component="p" fontSize={{xs: '1.2rem!important', sm: '1.2rem!important', md: '0.9rem!important'}} {...SlotProps?.Heading}>{Heading}</Typography>}
                {Body && <Typography color={SlotProps?.Body?.color || theme.palette.text.secondary} component="div" variant="body2" fontSize={{xs: '1rem!important', sm: '1rem!important', md: '1px'}} {...SlotProps?.Body}><TextBlockRenderer noParagraphSpacing={noParagraphSpacing} content={Body} /></Typography>}
                {TextItems && TextItems.length > 0 && <TextItemListComponent Items={TextItems} SlotProps={SlotProps?.TextItemList} />}
                {children && <Typography color={SlotProps?.Body?.color || theme.palette.text.secondary} component="div" variant="body2" {...SlotProps?.Body}>{children}</Typography>}
                {Links && Links.length > 0 && (
                    <Box mt={1}>
                        <LinkList
                            // LinkGroupHeaderLabelToIconMapping={iconMapping}
                            links={Links as LinkGroupProps[]}
                            ButtonProps={{
                                size: 'small'
                            }}
                            // forceButton
                            // requiredPublishType={requiredPublishType}
                            // LinkGroupItemsStackProps={{ gap: 1 }}
                            // LinkGroupItemsTypographyProps={{ sx: { fontSize: '1rem!important', color: theme.palette.text.primary, pl: 0.5 } }}
                            // LinkGroupHeaderLinkProps={{ sx: { ...navLinkStyle } }}
                            // LinkGroupHeaderTypographyProps={{ sx: navLinkStyle }}
                            StackProps={{ direction: "row", spacing: 2 }}
                            // StackProps={{ direction: "row", gap: { md: 1, lg: 2 } }}
                            relativeLinkComponent={GatsbyLink}
                        />
                    </Box>
                )}
            </Stack>
            {IconElement && headingAndButtonOnly ? <Stack  sx={{ height: '100%' }} direction={'row'} justifyContent={ 'space-between'} alignItems='center'>
                {IconElement}
            </Stack> :  undefined}
        </Stack>
    )
}

export const CardComponent = ({ Background, BorderColor, LinkUrl, SlotProps, ...other }: CardItemProps) => {
    const theme = useTheme();
    const cardStyle = useMemo(() => {
        return cardStyles(theme, Background, BorderColor, Boolean(LinkUrl))
    }, [Background, BorderColor, LinkUrl]);

    const cardComponentSlotProps = useMemo(() => {
        return {
            ...SlotProps,
            Background,
            BorderColor,
            LinkUrl,
            RootStack: {
                ...SlotProps?.RootStack,
                // alignItems: 'center',
                // justifyContent: 'flex-start'
            },
            ContainerStack: {
                ...SlotProps?.ContainerStack,
                alignItems: (SlotProps?.ContainerStack?.alignItems || { xs: other.Icon ? 'flex-start' : 'center', sm: other.Icon ? 'flex-start' : 'center', md: 'flex-start' }),
                sx: {
                    ...SlotProps?.ContainerStack?.sx
                }
            },
        }
    }, [SlotProps, LinkUrl, other.Icon, Background, BorderColor])

    return (
        LinkUrl ? <Link relativeLinkComponent={GatsbyLink} url={LinkUrl} forceLink ButtonProps={{ sx: { textDecoration: 'none!important', textTransform: 'none', textAlign: 'left', color: 'inherit', height: '100%' } }} LinkProps={{ sx: { textDecoration: 'none!important', color: 'inherit' } }}>
                <Box {...SlotProps?.Container} sx={{ ...cardStyle, ...(SlotProps?.Container?.sx as any) }}>
                    <CardComponentContent IconElement={!other.Icon ? <ChevronRight /> : undefined} {...other} SlotProps={cardComponentSlotProps} />
                </Box>
        </Link> :
            <Box {...SlotProps?.Container} sx={{ ...cardStyle, ...(SlotProps?.Container?.sx as any) }}><CardComponentContent {...other} SlotProps={cardComponentSlotProps} /></Box>
    )
}